import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Menu, Play, CheckCircle, Award, BookOpen, MessageCircle, X, Bot, User, Sparkles } from 'lucide-react';
import type { Course, CourseBlock } from '../../../src/lib/content-parser';
import { HeyGenAvatarSDK } from './HeyGenAvatarSDK';

// Alice AI Avatar Component
const AliceAvatar: React.FC<{ size?: 'sm' | 'md' | 'lg' }> = ({ size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-10 h-10',
    md: 'w-16 h-16',
    lg: 'w-24 h-24'
  };

  return (
    <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 p-1 animate-pulse`}>
      <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
        <div className="relative">
          <Bot className={`${size === 'lg' ? 'w-12 h-12' : size === 'md' ? 'w-8 h-8' : 'w-5 h-5'} text-purple-600`} />
          <Sparkles className={`absolute -top-1 -right-1 ${size === 'lg' ? 'w-5 h-5' : 'w-3 h-3'} text-pink-500`} />
        </div>
      </div>
    </div>
  );
};

// User Avatar Component
const UserAvatar: React.FC<{ name?: string; size?: 'sm' | 'md' | 'lg' }> = ({ name = 'You', size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base'
  };

  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold shadow-lg`}>
      {initials}
    </div>
  );
};

interface CourseViewerProps {
  courseId: string;
}

export const CourseViewer: React.FC<CourseViewerProps> = ({ courseId }) => {
  const [course, setCourse] = useState<Course | null>(null);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [completedLessons, setCompletedLessons] = useState<Set<string>>(new Set());
  const [showSidebar, setShowSidebar] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourse();
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const API_BASE = import.meta.env.VITE_API_BASE || 'https://techtree-lms.rr-startech-innovation.workers.dev/api';
      const response = await fetch(`${API_BASE}/content/course/${courseId}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setCourse(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch course:', error);
      setLoading(false);
    }
  };

  const currentLesson = course?.lessons[currentLessonIndex];
  const progress = course ? ((completedLessons.size / course.lessons.length) * 100) : 0;

  const handleNext = () => {
    if (course && currentLessonIndex < course.lessons.length - 1) {
      if (currentLesson) {
        setCompletedLessons(prev => new Set(prev).add(currentLesson.id));
      }
      setCurrentLessonIndex(currentLessonIndex + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    if (currentLessonIndex > 0) {
      setCurrentLessonIndex(currentLessonIndex - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleLessonSelect = (index: number) => {
    setCurrentLessonIndex(index);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (window.innerWidth < 1024) {
      setShowSidebar(false);
    }
  };

  const renderBlock = (block: CourseBlock, index: number) => {
    const animationDelay = `${index * 50}ms`;

    switch (block.type) {
      case 'title':
        return (
          <div
            className="animate-fadeIn mb-6 sm:mb-8"
            style={{ animationDelay }}
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4 leading-tight">
              <span className="gradient-text">{block.content.text}</span>
            </h1>
            <div className="h-1.5 sm:h-2 w-24 sm:w-32 gradient-bg rounded-full"></div>
          </div>
        );

      case 'text':
        return (
          <div
            className="animate-fadeIn mb-4 sm:mb-6"
            style={{ animationDelay }}
          >
            <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">
              {block.content.text}
            </p>
          </div>
        );

      case 'list':
        return (
          <div
            className="animate-fadeIn mb-6 sm:mb-8"
            style={{ animationDelay }}
          >
            <ul className="space-y-3 sm:space-y-4">
              {block.content.items.map((item: string, idx: number) => (
                <li key={idx} className="flex items-start group">
                  <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center mr-3 sm:mr-4 group-hover:scale-110 transition-transform">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <span className="text-base sm:text-lg text-gray-800 flex-1 pt-0.5 sm:pt-1">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        );

      case 'navigation':
        return (
          <div
            className="animate-slideInRight mb-3 sm:mb-4"
            style={{ animationDelay }}
          >
            <button className="w-full card-elevated p-4 sm:p-6 text-left group hover:shadow-glow transition-all">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                  <div className="relative flex-shrink-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    {block.metadata?.level && (
                      <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-lg">
                        {block.metadata.level}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-base sm:text-lg text-gray-900 truncate sm:whitespace-normal">{block.content.text}</div>
                    {block.content.description && (
                      <div className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-2 sm:line-clamp-none">{block.content.description}</div>
                    )}
                    {block.metadata?.isHotspot && (
                      <div className="text-xs text-purple-600 font-semibold mt-1 sm:mt-2">⚡ Interactive</div>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 group-hover:text-red-500 group-hover:translate-x-1 transition-all flex-shrink-0 ml-2" />
              </div>
            </button>
          </div>
        );

      case 'interactive':
        // Build lesson context for Alice
        const lessonContent = currentLesson?.blocks
          .filter(b => b.type === 'text' || b.type === 'list')
          .map(b => {
            if (b.type === 'text') return b.content.text;
            if (b.type === 'list') return b.content.items.join('; ');
            return '';
          })
          .join(' ');

        const lessonContext = {
          title: currentLesson?.title || 'Learning Session',
          description: currentLesson?.description,
          content: lessonContent,
          orderIndex: currentLesson?.orderIndex
        };

        return (
          <div
            className="animate-fadeIn mb-8"
            style={{ animationDelay }}
          >
            <div className="relative overflow-hidden card-elevated p-8 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 border-2 border-purple-200">
              <div className="absolute top-0 right-0 w-64 h-64 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" style={{ animationDelay: '1s' }}></div>

              <div className="relative">
                {/* HeyGen Avatar - Context-aware AI coach */}
                <HeyGenAvatarSDK
                  avatarId="Anna_public_20240108"
                  voiceId="1bd001e7e50f421d891986aad5158bc8"
                  lessonContext={lessonContext}
                  width="100%"
                  height="600px"
                  mobileWidth="100%"
                  mobileHeight="400px"
                  className="shadow-2xl"
                />
              </div>
            </div>
          </div>
        );

      case 'image':
        return (
          <div
            className="animate-fadeIn mb-8"
            style={{ animationDelay }}
          >
            <div className="card-elevated overflow-hidden">
              <img
                src={block.content.src}
                alt={block.content.alt || 'Course image'}
                className="w-full h-auto"
              />
              {block.content.caption && (
                <div className="p-4 bg-gray-50">
                  <p className="text-sm text-gray-600">{block.content.caption}</p>
                </div>
              )}
            </div>
          </div>
        );

      case 'video':
        return (
          <div
            className="animate-fadeIn mb-8"
            style={{ animationDelay }}
          >
            <div className="card-elevated overflow-hidden">
              <video
                src={block.content.src}
                controls
                className="w-full"
              />
              {block.content.title && (
                <div className="p-4 bg-gray-50">
                  <p className="font-semibold">{block.content.title}</p>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-red-50 via-purple-50 to-pink-50">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6">
            <div className="w-20 h-20 border-4 border-red-200 border-t-red-600 rounded-full animate-spin"></div>
          </div>
          <p className="text-xl font-semibold text-gray-700">Loading your course...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-6xl mb-4">📚</div>
          <div className="text-2xl font-bold text-gray-700">Course not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 via-red-50 to-orange-50 overflow-hidden">
      {/* Sidebar */}
      <div
        className={`${
          showSidebar ? 'translate-x-0' : '-translate-x-full'
        } fixed lg:relative z-50 w-full lg:w-96 h-full bg-white shadow-2xl transition-transform duration-300 ease-in-out flex flex-col`}
      >
        {/* Sidebar Header */}
        <div className="relative p-8 bg-gradient-to-br from-red-600 to-orange-600 text-white">
          <button
            onClick={() => setShowSidebar(false)}
            className="lg:hidden absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {/* User Profile */}
          <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-white/20">
            <UserAvatar name="You" size="lg" />
            <div>
              <div className="text-sm opacity-90">Welcome back,</div>
              <div className="font-bold text-lg">Learner</div>
            </div>
          </div>

          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-bold">{course.title}</h2>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between text-sm font-semibold mb-2">
              <span>Your Progress</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-3 bg-white/20 rounded-full overflow-hidden backdrop-blur-sm">
              <div
                className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-500 shadow-lg"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="mt-3 text-sm opacity-90">
              {completedLessons.size} of {course.lessons.length} lessons completed
            </div>
          </div>
        </div>

        {/* Lessons List */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-3">
            {course.lessons.map((lesson, index) => {
              const isCompleted = completedLessons.has(lesson.id);
              const isCurrent = index === currentLessonIndex;

              return (
                <button
                  key={lesson.id}
                  onClick={() => handleLessonSelect(index)}
                  className={`w-full text-left p-4 rounded-xl transition-all group ${
                    isCurrent
                      ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-lg scale-105'
                      : 'bg-gray-50 hover:bg-gray-100 text-gray-900'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                      isCurrent
                        ? 'bg-white/20'
                        : isCompleted
                        ? 'bg-green-100'
                        : 'bg-gray-200'
                    }`}>
                      {isCompleted ? (
                        <CheckCircle className={`w-6 h-6 ${isCurrent ? 'text-white' : 'text-green-600'}`} />
                      ) : (
                        <span className={`text-sm font-bold ${isCurrent ? 'text-white' : 'text-gray-600'}`}>
                          {index + 1}
                        </span>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold mb-1 ${isCurrent ? 'text-white' : 'text-gray-900'}`}>
                        {lesson.title}
                      </div>
                      {lesson.description && (
                        <div className={`text-sm ${isCurrent ? 'text-white/80' : 'text-gray-600'}`}>
                          {lesson.description}
                        </div>
                      )}
                      {lesson.estimatedDuration && (
                        <div className={`text-xs mt-2 ${isCurrent ? 'text-white/70' : 'text-gray-500'}`}>
                          ⏱️ {lesson.estimatedDuration} min
                        </div>
                      )}
                    </div>

                    {isCurrent && (
                      <Play className="w-5 h-5 text-white animate-pulse" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Achievement Badge */}
        {progress === 100 && (
          <div className="p-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <UserAvatar name="You" size="lg" />
                <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg">
                  <Award className="w-5 h-5 text-green-600" />
                </div>
              </div>
              <div>
                <div className="font-bold text-lg">Course Complete!</div>
                <div className="text-sm opacity-90">Congratulations on finishing</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Navigation Bar */}
        <div className="flex-shrink-0 bg-white/80 backdrop-blur-md shadow-md border-b border-gray-200">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setShowSidebar(!showSidebar)}
                className="p-3 hover:bg-gray-100 rounded-xl transition-colors group"
              >
                <Menu className="w-6 h-6 text-gray-700 group-hover:text-red-600 transition-colors" />
              </button>

              <div className="flex items-center space-x-4">
                <button
                  onClick={handlePrevious}
                  disabled={currentLessonIndex === 0}
                  className={`p-3 rounded-xl transition-all ${
                    currentLessonIndex === 0
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'hover:bg-gray-100 text-gray-700 hover:scale-110'
                  }`}
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>

                <div className="px-6 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-full font-semibold shadow-lg">
                  {currentLessonIndex + 1} / {course.lessons.length}
                </div>

                <button
                  onClick={handleNext}
                  disabled={currentLessonIndex === course.lessons.length - 1}
                  className={`p-3 rounded-xl transition-all ${
                    currentLessonIndex === course.lessons.length - 1
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'hover:bg-gray-100 text-gray-700 hover:scale-110'
                  }`}
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Lesson Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-5xl mx-auto p-4 sm:p-6 md:p-8 lg:p-12">
            {currentLesson && (
              <div className="card-elevated p-4 sm:p-6 md:p-8 lg:p-12 bg-white/80 backdrop-blur-sm">
                {/* Render lesson blocks */}
                {currentLesson.blocks.map((block, index) => (
                  <div key={block.id}>
                    {renderBlock(block, index)}
                  </div>
                ))}

                {/* Navigation Buttons */}
                <div className="mt-12 pt-8 border-t-2 border-gray-200 flex flex-col sm:flex-row justify-between gap-4">
                  <button
                    onClick={handlePrevious}
                    disabled={currentLessonIndex === 0}
                    className={`px-8 py-4 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 ${
                      currentLessonIndex === 0
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700 hover:shadow-lg hover:scale-105'
                    }`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                    <span>Previous Lesson</span>
                  </button>

                  <button
                    onClick={handleNext}
                    className={`px-8 py-4 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 ${
                      currentLessonIndex === course.lessons.length - 1
                        ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:shadow-glow'
                        : 'bg-gradient-to-r from-red-500 to-orange-500 text-white hover:shadow-glow'
                    } hover:scale-105`}
                  >
                    <span>
                      {currentLessonIndex === course.lessons.length - 1
                        ? 'Complete Course'
                        : 'Next Lesson'}
                    </span>
                    {currentLessonIndex === course.lessons.length - 1 ? (
                      <Award className="w-5 h-5" />
                    ) : (
                      <ChevronRight className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {showSidebar && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={() => setShowSidebar(false)}
        />
      )}
    </div>
  );
};